// ────────────────────────────────────────────────────────────────────────────
// The TMS navigable areas an organisation can switch on or off.
//
// Stored on the Directory `Organisation` as `tmsMenuJson` and edited by Stratum
// support from the Modules card on Central Configuration. The keys mirror the server's
// `AppCapabilities.TmsNavigable` exactly — that is the contract, and it is what
// lets one stored fact drive the sidebar, the route guards and the
// functional-role picker together.
//
// THIS FILE IS FOR EDITING THE SETTING, NOT FOR READING IT. Nothing that draws
// a screen should branch on these: the same setting narrows the capability list
// the server returns, and `usePermissions().can(...)` is the single question
// every gate asks. A second source for one answer is exactly how a menu and a
// route guard come to disagree.
//
// EXCEPT the opt-in modules (TMS Mobile), which are OFF until set true — see
// OPT_IN_MODULES below.
//
// A MISSING KEY MEANS ON, matching the server, so a null column means the whole
// menu and an area added in a later build is on for every organisation that
// saved before it existed.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Mirrors `AppCapabilities.TmsNavigable`, in sidebar order.
 *
 * <p>
 * Settings keys are deliberately absent. They are not switchable, which is what
 * keeps Central Configuration — the screen these switches live on — reachable
 * whatever anybody turns off.
 * </p>
 */
export const NAVIGABLE_AREAS = [
  'dashboard',
  'orders',
  'analytics',
  'roster',
  'locations',
  'customers',
  'suppliers',
  'invoicing',
] as const;

export type NavigableArea = (typeof NAVIGABLE_AREAS)[number];

/**
 * Mirrors `AppCapabilities.TmsOptInModules`: modules an organisation takes up,
 * so the OPPOSITE default — off unless the stored map says `true`.
 *
 *   - `mobile` — TMS Mobile: Trip Checkpoints sent to drivers in myDriver, and
 *     the Mobile Users tab.
 */
export const OPT_IN_MODULES = ['mobile'] as const;

export type OptInModule = (typeof OPT_IN_MODULES)[number];

export type ModuleKey = NavigableArea | OptInModule;

/** Every switch on the Modules card, in card order. */
export const MODULES: readonly ModuleKey[] = [...NAVIGABLE_AREAS, ...OPT_IN_MODULES];

export type MenuAreaMap = Record<ModuleKey, boolean>;

/**
 * The shipped default and the fallback for unreadable input: every navigable
 * area on, every opt-in module off.
 */
export function allAreasEnabled(): MenuAreaMap {
  return Object.fromEntries([
    ...NAVIGABLE_AREAS.map((a) => [a, true]),
    ...OPT_IN_MODULES.map((m) => [m, false]),
  ]) as MenuAreaMap;
}

/**
 * Read the stored map into a complete, every-area-present record.
 *
 * <p>
 * Never throws and never returns a partial map. Anything unreadable falls back
 * to every area on — the same posture the server takes, and for the same
 * reason: a menu that cannot be parsed must not become a menu that is empty.
 * </p>
 */
export function parseMenuAreas(json: string | null | undefined): MenuAreaMap {
  const map = allAreasEnabled();
  if (!json) return map;

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return map;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;

  const stored = raw as Record<string, unknown>;
  for (const area of NAVIGABLE_AREAS) {
    // Only an explicit `false` takes an area away, exactly as `TmsMenuDocument`
    // reads it server-side.
    if (stored[area] === false) map[area] = false;
  }
  for (const module of OPT_IN_MODULES) {
    // And only an explicit `true` turns an opt-in module on.
    if (stored[module] === true) map[module] = true;
  }
  return map;
}

/** The wire value — always the complete map, so the column says what it means. */
export function serialiseMenuAreas(map: MenuAreaMap): string {
  return JSON.stringify(
    Object.fromEntries([
      ...NAVIGABLE_AREAS.map((a) => [a, map[a] !== false]),
      ...OPT_IN_MODULES.map((m) => [m, map[m] === true]),
    ]),
  );
}
